import { create } from 'zustand';
import { Permissions } from "ts-interfaces";
import { getBoardsScheme, renameBoard as renameBoardApi, copyBoard, deleteBoard as deleteBoardApi, getSharedBoards } from '../v2/apis/boardsSchemeApi';
import { BoardSchemeInterface } from 'ts-interfaces';

interface BoardsState {
  boards: BoardSchemeInterface[];
  userBoards: BoardSchemeInterface[];
  sharedBoards: BoardSchemeInterface[];
  activeBoards: BoardSchemeInterface[];
  isLoading: boolean;
  lastDoc: any;
  hasMore: boolean;
  permissionsRef: Permissions | null;
  initialFetchDone: boolean;
  sharedFetchDone: boolean;
  currentUserId: string | null;
  sharedBoardIds: string[];

  setBoards: (boards: BoardSchemeInterface[]) => void;
  getNext: (firstPageOnly?: boolean, pageSize?: number) => void;
  getBoards: (userId: string, permissions: Permissions | null, pageSize?: number) => {
    boards: BoardSchemeInterface[];
    setBoards: (boards: BoardSchemeInterface[]) => void;
    getNext: () => void;
    isLoading: boolean;
    hasMore: boolean;
  };
  getBoardsOptimized: (userId: string, permissions: Permissions | null, pageSize?: number) => {
    boards: BoardSchemeInterface[];
    setBoards: (boards: BoardSchemeInterface[]) => void;
    getNext: () => void;
    isLoading: boolean;
    hasMore: boolean;
  };
  refreshSharedBoards: (permissions: Permissions) => Promise<void>;
  syncBoardAssociations: (boardsToAdd: string[], boardsToRemove: string[], projectId: string) => void;
  resetBoards: () => void;
}

export const useBoards = create<BoardsState>((set, get) => ({
  boards: [],
  isLoading: false,
  userBoards: [],
  sharedBoards: [],
  activeBoards: [],
  lastDoc: null,
  hasMore: true,
  permissionsRef: null,
  initialFetchDone: false,
  sharedFetchDone: false,
  currentUserId: null,
  sharedBoardIds: [],

  setBoards: (boards) => set(state => {
  const showArchived = window.location.search.includes('archive');
  const activeBoards = showArchived ? boards : boards.filter(board => !board.archive);
  const userBoards = activeBoards.filter(board => (board.user === state.currentUserId));
  
  // Preserve existing shared boards instead of trying to derive from input
  const filteredSharedBoards = showArchived 
    ? state.sharedBoards 
    : state.sharedBoards.filter(board => !board.archive);
  
  // Combine user boards with existing shared boards for activeBoards
  const allActiveBoards = [...userBoards, ...filteredSharedBoards];
  
  return { 
    boards, 
    activeBoards: allActiveBoards, 
    userBoards, 
    sharedBoards: filteredSharedBoards 
  };
}),
  
  getNext: (firstPageOnly = false, pageSize = 15) => {
    const { isLoading, hasMore, lastDoc, boards, currentUserId: userId } = get();
    
    if (isLoading || !hasMore || !userId) return;
    if (firstPageOnly && lastDoc) return;
    
    set({ isLoading: true });
    
    // Fetch boards asynchronously
    (async () => {
      try {
        const { boards: newBoards, lastVisible } = await getBoardsScheme(
          lastDoc, userId, pageSize
        );
        set(state => {
          const updatedBoards = lastDoc 
            ? [...state.boards, ...newBoards.filter(board => 
                !state.boards.some(b => b.id === board.id))]
            : newBoards;
            
          const showArchived = window.location.search.indexOf('archive') > 0;
          const activeBoards = showArchived ? updatedBoards : updatedBoards.filter(board => !board.archive);
          const userBoards = activeBoards.filter((board:any) => board.user === state.currentUserId);
          
          // Combine with shared boards to update activeBoards
          const allActiveBoards = [...userBoards, ...state.sharedBoards];
          
          return {
            hasMore: newBoards.length >= pageSize,
            boards: updatedBoards,
            activeBoards: allActiveBoards,
            userBoards,
            lastDoc: lastVisible,
            isLoading: false
          };
        });
      } catch (error) {
        console.error("Error fetching boards:", error);
        set({ isLoading: false });
      }
    })();
  },
  
  // New method to fetch only shared boards when permissions change
  refreshSharedBoards: async (permissions: Permissions) => {
    if (!permissions) return;
    
    const currentSharedIds = [...new Set([
      ...(permissions.boardRead || []), 
      ...(permissions.boardWrite || [])
    ])];
    
    try {
      const sharedBoards = await getSharedBoards(permissions);
      const showArchived = window.location.search.indexOf('archive') > 0;
      const filteredSharedBoards = showArchived ? sharedBoards : sharedBoards.filter(board => !board.archive);
      
      set(state => {
        // Combine user boards with new shared boards
        const allActiveBoards = [...state.userBoards, ...filteredSharedBoards];
        
        return {
          sharedBoards: filteredSharedBoards,
          sharedBoardIds: currentSharedIds,
          activeBoards: allActiveBoards,
          sharedFetchDone: true
        };
      });
    } catch (error) {
      console.error("Error refreshing shared boards:", error);
    }
  },
  
  getBoards: (userId, permissions, pageSize = 15) => {
    const state = get();

    // Check if we need to fetch shared boards (permissions changed or not fetched yet)
    const currentSharedIds = permissions 
      ? [...new Set([
          ...(permissions.boardRead || []), 
          ...(permissions.boardWrite || [])
        ])]
      : [];
    const sharedIdsChanged = JSON.stringify(currentSharedIds.sort()) !== JSON.stringify(state.sharedBoardIds.sort());
    const needSharedFetch = !state.sharedFetchDone || sharedIdsChanged;

    // Handle permission changes for already initialized state
    if (state.initialFetchDone && needSharedFetch && permissions && userId === state.currentUserId) {
      get().refreshSharedBoards(permissions);
    }
    
    // Initialize fetch if needed (first time or user change)
    if ((!state.initialFetchDone || userId !== state.currentUserId) && userId) {
      set({ currentUserId: userId, permissionsRef: permissions });
      
      // Start async fetch process
      (async () => {
        // Fetch shared boards if needed
        if (needSharedFetch && permissions) {
          await get().refreshSharedBoards(permissions);
        }
        
        // Fetch user boards
        set({ lastDoc: null, hasMore: true });
        get().getNext(true, pageSize);
        set({ initialFetchDone: true });
      })().catch(error => console.error("Error initializing boards:", error));
    }
    
    // Return current state
    return {
      boards: get().boards,
      activeBoards: get().activeBoards,
      userBoards: get().userBoards,
      sharedBoards: get().sharedBoards,
      setBoards: get().setBoards,
      getNext: () => get().getNext(false, pageSize),
      isLoading: get().isLoading,
      hasMore: get().hasMore
    };
  },

  getBoardsOptimized: (userId, permissions, pageSize = 15) => {
      const state = get();
      
      // Skip if already loading or no userId
      if (state.isLoading || !userId) {
          return state; // Return current state as-is
      }

      // Case 1: Initial load - fetch user's own boards
      if (!state.initialFetchDone) {
          set({ 
              currentUserId: userId,
              permissionsRef: permissions
          });
          
          // Fetch user boards
          get().getNext(true, pageSize);
          set({ initialFetchDone: true });
          
          // Fetch shared boards if permissions available
          if (permissions) {
              get().refreshSharedBoards(permissions);
          }
      } 
      // Case 2: Already initialized - check if we need to update shared boards
      else if (permissions && state.permissionsRef !== permissions) {
          set({ permissionsRef: permissions });
          get().refreshSharedBoards(permissions);
      }
      
      // Return interface to current state
      return {
          boards: state.boards,
          activeBoards: state.activeBoards,
          userBoards: state.userBoards,
          sharedBoards: state.sharedBoards,
          setBoards: state.setBoards,
          getNext: () => get().getNext(false, pageSize),
          isLoading: state.isLoading,
          hasMore: state.hasMore
      };
  },

  syncBoardAssociations: (boardsToAdd: string[], boardsToRemove: string[], projectId: string) => {
    set(state => {
      const updateBoard = (board) => {
        if (boardsToAdd.includes(board.id)) {
          return { ...board, associatedProjectId: projectId };
        }
        if (boardsToRemove.includes(board.id)) {
          return { ...board, associatedProjectId: null };
        }
        return board;
      };

      // Apply the updateBoard function to all board arrays
      const updatedBoards = state.boards.map(updateBoard);
      const updatedUserBoards = state.userBoards.map(updateBoard);
      const updatedSharedBoards = state.sharedBoards.map(updateBoard);
      
      // Recalculate activeBoards with updated data
      const showArchived = window.location.search.includes('archive');
      const filteredUserBoards = showArchived ? updatedUserBoards : updatedUserBoards.filter(board => !board.archive);
      const filteredSharedBoards = showArchived ? updatedSharedBoards : updatedSharedBoards.filter(board => !board.archive);
      const allActiveBoards = [...filteredUserBoards, ...filteredSharedBoards];

      return {
        boards: updatedBoards,
        userBoards: updatedUserBoards,
        sharedBoards: updatedSharedBoards,
        activeBoards: allActiveBoards
      };
    });
  },

  resetBoards: () => set(state => ({
    boards: [],
    userBoards: [],
    sharedBoards: [],
    activeBoards: [],
    isLoading: false,
    lastDoc: null,
    hasMore: true,
    initialFetchDone: false,
    sharedFetchDone: false,
    sharedBoardIds: []
  }))

}));

